import { useState, useEffect, useCallback } from 'react';
import { offerAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { HorizontalScrollTable } from '../components/ui/horizontal-scroll-table';
import { Loader2, FileText, ArrowUpDown, ArrowUp, ArrowDown, Search, ShoppingCart, TrendingUp, Archive } from 'lucide-react';

const STATUS_COLORS = {
  NEW: 'bg-blue-100 text-blue-800',
  REVIEWED: 'bg-indigo-100 text-indigo-800',
  APPROVED: 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-red-100 text-red-800',
  COUNTERED: 'bg-amber-100 text-amber-800',
  CONVERTED_TO_PURCHASE: 'bg-purple-100 text-purple-800',
  EXPIRED: 'bg-red-100 text-red-800',
};

// 21 Oct 2026: Offers & Purchases is retired. This page is now a Super-Admin-only,
// read-only history of offers created before retirement — no create/approve/
// reject/counter/import actions remain.
export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offerType, setOfferType] = useState('PURCHASE');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('offer_number');
  const [sortOrder, setSortOrder] = useState('desc');
  const [detailModal, setDetailModal] = useState(null);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { offer_type: offerType, limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await offerAPI.list(params);
      setOffers(res.data || []);
    } catch (e) {
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  }, [offerType, statusFilter]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('desc'); }
  };

  const filtered = offers.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.offer_number || '').toLowerCase().includes(q) ||
      (o.sku_code || '').toLowerCase().includes(q) ||
      (o.sku_name || '').toLowerCase().includes(q) ||
      (o.supplier_name || '').toLowerCase().includes(q) ||
      (o.customer_name || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortField] || '', vb = b[sortField] || '';
    if (typeof va === 'number') return sortOrder === 'asc' ? va - vb : vb - va;
    return sortOrder === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-primary" /> : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };
  const SH = ({ field, children, className = '' }) => (
    <TableHead className={`font-medium text-xs uppercase cursor-pointer hover:bg-muted/80 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center">{children}<SortIcon field={field} /></div>
    </TableHead>
  );

  return (
    <div className="space-y-6" data-testid="offers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Offers</h1>
          <p className="text-muted-foreground text-sm flex items-center gap-1.5">
            <Archive className="h-3.5 w-3.5" />Retired — read-only history, Super Admin only
          </p>
        </div>
      </div>

      {/* Offer Type Tabs */}
      <div className="flex gap-1 border-b border-border">
        <button onClick={() => setOfferType('PURCHASE')} data-testid="tab-purchase"
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${offerType === 'PURCHASE' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          <ShoppingCart className="h-4 w-4" />Purchase Offers
        </button>
        <button onClick={() => setOfferType('SALES')} data-testid="tab-sales"
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${offerType === 'SALES' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          <TrendingUp className="h-4 w-4" />Sales Offers
        </button>
      </div>

      {/* Filters */}
      <Card className="rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search offer #, SKU, name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="offer-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 rounded-sm" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="NEW">New</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
                <SelectItem value="COUNTERED">Countered</SelectItem>
                <SelectItem value="CONVERTED_TO_PURCHASE">Converted</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-sm">
        <CardContent className="p-0">
          <HorizontalScrollTable>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <SH field="offer_number">Offer #</SH>
                <SH field="sku_code">SKU</SH>
                <SH field="sku_name" className="min-w-[160px]">Item</SH>
                <TableHead className="font-medium text-xs uppercase">{offerType === 'PURCHASE' ? 'Vendor' : 'Customer'}</TableHead>
                <SH field="qty_offered" className="text-right">Qty</SH>
                <SH field="ask_price_usd" className="text-right">Price (USD)</SH>
                <TableHead className="font-medium text-xs uppercase">Submitter</TableHead>
                <SH field="status">Status</SH>
                <SH field="date_time">Created</SH>
                <TableHead className="font-medium text-xs uppercase">Approved</TableHead>
                <TableHead className="font-medium text-xs uppercase">Valid Till</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></TableCell></TableRow>
              ) : sorted.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8"><FileText className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" /><p className="text-muted-foreground">No offers found</p></TableCell></TableRow>
              ) : sorted.map(offer => (
                <TableRow key={offer.id} className="table-row-hover cursor-pointer" onClick={() => setDetailModal(offer)} data-testid={`offer-row-${offer.offer_number}`}>
                  <TableCell className="font-mono text-xs font-medium text-primary hover:underline" data-testid={`offer-num-${offer.offer_number}`}>
                    {offer.offer_number}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{offer.sku_code}</TableCell>
                  <TableCell className="text-sm max-w-[180px] truncate">{offer.sku_name || offer.sku_id}</TableCell>
                  <TableCell className="text-sm">{offerType === 'PURCHASE' ? offer.supplier_name : offer.customer_name || '—'}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{offer.qty_offered}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatCurrency(offer.ask_price_usd)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{offer.created_by_name || offer.created_by_email || '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs rounded-sm ${STATUS_COLORS[offer.status] || ''}`}>
                      {offer.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{offer.date_time ? new Date(offer.date_time).toLocaleString(undefined, {day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</TableCell>
                  <TableCell className="text-xs">{offer.approved_at ? new Date(offer.approved_at).toLocaleString(undefined, {day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs">
                    {offer.offer_valid_till ? (
                      <span className="text-muted-foreground">
                        {new Date(offer.offer_valid_till).toLocaleDateString(undefined, {day:'2-digit',month:'short',year:'numeric'})}
                      </span>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </HorizontalScrollTable>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">Showing {sorted.length} {offerType.toLowerCase()} offers</p>

      {/* Detail Modal */}
      {detailModal && (
        <OfferDetailModal offer={detailModal} open={!!detailModal} onClose={() => setDetailModal(null)} />
      )}
    </div>
  );
}

/* ===================== OFFER DETAIL MODAL (read-only) ===================== */
function OfferDetailModal({ offer, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && offer?.id) {
      setLoading(true);
      offerAPI.get(offer.id).then(r => setDetail(r.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [open, offer?.id]);

  const o = detail || offer;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {o.offer_number}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Type:</span> <strong>{o.offer_type}</strong></div>
              <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className={`text-xs ${STATUS_COLORS[o.status] || ''}`}>{o.status}</Badge></div>
              <div><span className="text-muted-foreground">SKU:</span> <span className="font-mono">{o.sku_code}</span></div>
              <div><span className="text-muted-foreground">Item:</span> {o.sku_name}</div>
              <div><span className="text-muted-foreground">Qty:</span> <strong>{o.qty_offered}</strong></div>
              <div><span className="text-muted-foreground">Price:</span> <strong>{formatCurrency(o.ask_price_usd)}</strong></div>
              <div><span className="text-muted-foreground">{o.offer_type === 'PURCHASE' ? 'Vendor' : 'Customer'}:</span> {o.supplier_name || o.customer_name || '—'}</div>
              <div><span className="text-muted-foreground">Submitted by:</span> {o.created_by_name || o.created_by_email || '—'}</div>
            </div>
            {o.notes && <div className="p-3 bg-muted rounded-sm text-sm"><strong>Notes:</strong> {o.notes}</div>}

            {/* History Timeline */}
            {o.history?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">History</p>
                <div className="space-y-2 border-l-2 border-border pl-4">
                  {o.history.map((h, i) => (
                    <div key={i} className="relative" data-testid={`history-${i}`}>
                      <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-primary border-2 border-background" />
                      <div className="text-sm">
                        <span className="font-medium">{h.action}</span>
                        <span className="text-muted-foreground"> by {h.by_name || 'System'}</span>
                        <span className="text-xs text-muted-foreground ml-2">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
                      </div>
                      {(h.counter_price || h.new_price || h.price) && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Price: ${h.counter_price || h.new_price || h.price}
                          {(h.counter_qty || h.new_qty || h.qty) ? ` | Qty: ${h.counter_qty || h.new_qty || h.qty}` : ''}
                        </p>
                      )}
                      {(h.notes || h.counter_notes) && <p className="text-xs text-muted-foreground">{h.notes || h.counter_notes}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
